import { getAllValuesForTasField } from '../../../utils/tuition-assistance/fields.ts';
import Component from '@glimmer/component';
import { trackedSet } from '@ember/reactive/collections';
import { assert } from '@ember/debug';
import { t } from 'ember-intl';
import RadioGroup from 'tio-ui/components/radio-group';
import type TASProgramTemplateModel from 'tio-data/models/tas-program-template';
import type Owner from '@ember/owner';
interface TuitionAssistanceApplicationDetailsCoursesApprovalWaiverChecklistSignature {
  Args: {
    programTemplate: TASProgramTemplateModel;
    didChange: ({
      hasConfirmedEvidenceApprovalWaiver,
      hasConfirmedEvidenceAttachmentWaiver,
      hasRespondedToAllChecklistItems,
    }: {
      hasConfirmedEvidenceApprovalWaiver: boolean;
      hasConfirmedEvidenceAttachmentWaiver: boolean;
      hasRespondedToAllChecklistItems: boolean;
    }) => void;
  };
  Element: HTMLElement;
}

interface CoursesApprovalWaiverChecklistItemRadioGroupSignature {
  Args: {
    item: string;
    onChange: (item: string, value: boolean) => void;
  };
}

class CoursesApprovalWaiverChecklistItemRadioGroup extends Component<CoursesApprovalWaiverChecklistItemRadioGroupSignature> {
  onChange = (value: boolean) => {
    this.args.onChange(this.args.item, value);
  };

  <template>
    <li>
      <RadioGroup
        @label={{@item}}
        @isRequired={{true}}
        @orientation="horizontal"
        @onChange={{this.onChange}}
        @size="md"
        class="tio-row-between flex-row gap-4"
        as |Radio|
      >
        <Radio @value={{false}} @label={{t "common.yes"}} />
        <Radio @value={{true}} @label={{t "common.no"}} />
      </RadioGroup>
    </li>
  </template>
}

class TuitionAssistanceApplicationDetailsCoursesApprovalWaiverChecklistComponent extends Component<TuitionAssistanceApplicationDetailsCoursesApprovalWaiverChecklistSignature> {
  confirmedEvidenceAttachmentWaiverItems = trackedSet<string>();
  confirmedEvidenceApprovalWaiverItems = trackedSet<string>();
  declinedEvidenceAttachmentWaiverItems = trackedSet<string>();
  declinedEvidenceApprovalWaiverItems = trackedSet<string>();

  evidenceAttachmentWaiverItems: Set<string> = new Set();
  evidenceApprovalWaiverItems: Set<string> = new Set();

  constructor(
    owner: Owner,
    args: TuitionAssistanceApplicationDetailsCoursesApprovalWaiverChecklistSignature['Args']
  ) {
    super(owner, args);
    this.evidenceAttachmentWaiverItems = new Set(
      getAllValuesForTasField(
        'EVIDENCE_ATTACHMENT_WAIVER_CHECKLIST',
        this.args.programTemplate.fields
      ) as string[]
    );
    this.evidenceApprovalWaiverItems = new Set(
      getAllValuesForTasField(
        'EVIDENCE_APPROVAL_WAIVER_CHECKLIST',
        this.args.programTemplate.fields
      ) as string[]
    );

    if (this.evidenceAttachmentWaiverItems.size <= 0) {
      assert(
        'TuitionAssistanceApplicationDetailsCoursesApprovalWaiverChecklistComponent: EVIDENCE_ATTACHMENT_WAIVER_CHECKLIST must have at least one item'
      );
    }
    if (this.evidenceApprovalWaiverItems.size <= 0) {
      assert(
        'TuitionAssistanceApplicationDetailsCoursesApprovalWaiverChecklistComponent: EVIDENCE_APPROVAL_WAIVER_CHECKLIST must have at least one item'
      );
    }
  }

  get hasConfirmedAllEvidenceApprovalWaiverItems() {
    return (
      this.confirmedEvidenceApprovalWaiverItems.size === this.evidenceApprovalWaiverItems.size &&
      this.hasConfirmedAllEvidenceAttachmentWaiverItems &&
      this.hasRespondedToAllChecklistItems
    );
  }

  get hasConfirmedAllEvidenceAttachmentWaiverItems() {
    return (
      this.confirmedEvidenceAttachmentWaiverItems.size ===
        this.evidenceAttachmentWaiverItems.size && this.hasRespondedToAllChecklistItems
    );
  }

  get hasRespondedToAllChecklistItems() {
    const allRespondedItemsLength =
      this.confirmedEvidenceApprovalWaiverItems.size +
      this.declinedEvidenceApprovalWaiverItems.size +
      this.confirmedEvidenceAttachmentWaiverItems.size +
      this.declinedEvidenceAttachmentWaiverItems.size;

    const allChecklistItemsLength =
      this.evidenceApprovalWaiverItems.size + this.evidenceAttachmentWaiverItems.size;

    return allRespondedItemsLength > 0 && allRespondedItemsLength === allChecklistItemsLength;
  }

  toggleConfirmationOfAttachmentWaiverItem = (item: string, value: boolean) => {
    this.toggleConfirmationItem(
      item,
      value,
      this.confirmedEvidenceAttachmentWaiverItems,
      this.declinedEvidenceAttachmentWaiverItems,
      this.evidenceAttachmentWaiverItems
    );
  };

  toggleConfirmationOfApprovalWaiverItem = (item: string, value: boolean) => {
    this.toggleConfirmationItem(
      item,
      value,
      this.confirmedEvidenceApprovalWaiverItems,
      this.declinedEvidenceApprovalWaiverItems,
      this.evidenceApprovalWaiverItems
    );
  };

  toggleConfirmationItem(
    item: string,
    value: boolean,
    confirmedSet: Set<string>,
    declinedSet: Set<string>,
    checklistSet: Set<string>
  ) {
    if (!checklistSet.has(item)) {
      console.error(
        `TuitionAssistanceApplicationDetailsCoursesApprovalWaiverChecklistComponent: ${item} is not a valid checklist item for program template ${this.args.programTemplate.id}`
      );
      return;
    }

    if (value) {
      confirmedSet.add(item);
      declinedSet.delete(item);
    } else {
      confirmedSet.delete(item);
      declinedSet.add(item);
    }
    this.didChangeConfirmation();
  }

  didChangeConfirmation = () => {
    this.args.didChange({
      hasConfirmedEvidenceApprovalWaiver: this.hasConfirmedAllEvidenceApprovalWaiverItems,
      hasConfirmedEvidenceAttachmentWaiver: this.hasConfirmedAllEvidenceAttachmentWaiverItems,
      hasRespondedToAllChecklistItems: this.hasRespondedToAllChecklistItems,
    });
  };

  <template>
    <ul class="list-disc pl-6 pr-2 space-y-2" ...attributes>
      {{#each this.evidenceAttachmentWaiverItems as |item|}}
        <CoursesApprovalWaiverChecklistItemRadioGroup
          @item={{item}}
          @onChange={{this.toggleConfirmationOfAttachmentWaiverItem}}
        />
      {{/each}}

      {{#each this.evidenceApprovalWaiverItems as |item|}}
        <CoursesApprovalWaiverChecklistItemRadioGroup
          @item={{item}}
          @onChange={{this.toggleConfirmationOfApprovalWaiverItem}}
        />
      {{/each}}
    </ul>
  </template>
}

export default TuitionAssistanceApplicationDetailsCoursesApprovalWaiverChecklistComponent;
